/**
 * Product Recommendation Modal System
 * Standalone implementation with dynamic HTML generation
 */

class RecommendationModal {
  constructor(options = {}) {
    // Configuration
    this.settings = {
      apiEndpoint: options.apiEndpoint || 'https://categories-top.vercel.app/api/upsell',
      cartUrl: options.cartUrl || '/cart',
      cartAddUrl: options.cartAddUrl || '/cart/add',
      messages: {
        itemAdded: options.translations?.itemAdded || 'Item added to your bag.',
        recommendedProducts: options.translations?.recommendedProducts || 'Recommended products',
        bestSellers: options.translations?.bestSellers || 'Best Sellers',
        featured: options.translations?.featured || 'The Featured',
        newArrivals: options.translations?.newArrivals || 'New Arrivals',
        viewBag: options.translations?.viewBag || 'View Bag',
        checkout: options.translations?.checkout || 'Check out',
        loading: options.translations?.loading || 'Loading...',
        noProducts: options.translations?.noProducts || 'No products found',
        addToCart: options.translations?.addToCart || 'Add to cart',
        addedToCart: options.translations?.addedToCart || '✓ Added to cart!',
        alreadyInCart: options.translations?.alreadyInCart || 'This item is already in your cart',
        error: options.translations?.error || 'Could not add product to cart. Please try again.',
        ...options.translations
      }
    };

    // Product detection
    this.product = this.detectProduct();
    
    // Data management
    this.data = {
      bestsellers: [],
      featured: [],
      newarrivals: []
    };
    this.loaded = false;
    this.loading = false; // Loading state flag to prevent concurrent requests
    this.loadingPromise = null; // Promise for ongoing request
    this.cache = {
      bestsellers: null,
      featured: null,
      newarrivals: null
    };
    this.addedItems = new Set();
    this.cartCount = this.detectCartCount();
    
    // Cache configuration (24 hours expiry)
    // Since products are not added frequently, recommendation data doesn't change often.
    // Caching for 24 hours can:
    // 1. Significantly reduce API calls (reduce server load, ~95% fewer calls)
    // 2. Improve user experience (instant display on refresh, 0 delay)
    // 3. Save bandwidth and costs
    // 4. Suitable for scenarios where products are not added frequently
    this.cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours (1 day)
    this.cacheKey = 'rec_modal_cache';

    // DOM references (only for cart notification injection, no popup)
    this.notification = null;

    // Initialize
    this.setup();
  }

  /**
   * Detect current product information
   */
  detectProduct() {
    // Method 1: Shopify global object
    if (window.Shopify && window.Shopify.theme && window.Shopify.theme.product) {
      return {
        id: window.Shopify.theme.product.id,
        handle: window.Shopify.theme.product.handle
      };
    }

    // Method 2: URL parsing
    const pathMatch = window.location.pathname.match(/\/products\/([^\/]+)/);
    if (pathMatch) {
      return {
        handle: pathMatch[1]
      };
    }

    // Method 3: Meta tag
    const productHandleMeta = document.querySelector('meta[property="product:handle"]');
    if (productHandleMeta) {
      return {
        handle: productHandleMeta.content
      };
    }

    // Method 4: Data attribute
    const productDataEl = document.querySelector('[data-product-handle]');
    if (productDataEl) {
      return {
        handle: productDataEl.dataset.productHandle,
        id: productDataEl.dataset.productId
      };
    }

    console.warn('[REC MODAL] Could not detect current product');
    return null;
  }

  /**
   * Detect cart count
   */
  detectCartCount() {
    // Method 1: Shopify global
    if (window.Shopify && window.Shopify.theme && window.Shopify.theme.cart) {
      return window.Shopify.theme.cart.item_count || 0;
    }

    // Method 2: Page elements
    const cartCountEl = document.querySelector('.cart-count, [data-cart-count], #cart-icon-bubble .cart-count-bubble span');
    if (cartCountEl) {
      const count = parseInt(cartCountEl.textContent, 10);
      return isNaN(count) ? 0 : count;
    }

    return 0;
  }

  /**
   * Setup - Create HTML structure
   */
  setup() {
    if (!this.product || !this.product.handle) {
      console.warn('[REC MODAL] No product handle found, modal will not work');
      return;
    }

    this.buildHTML();
    this.attachEvents();
    
    // Preload recommendation data immediately on page load (optimize user experience)
    this.preload();
  }

  /**
   * Preload recommendation data in background
   * Preload recommendation data in background without blocking the page
   */
  async preload() {
    if (this.loaded || this.loading || !this.product) return;
    
    // Delay briefly to let page render first (use requestIdleCallback if available)
    if (window.requestIdleCallback) {
      await new Promise(resolve => {
        window.requestIdleCallback(() => {
          setTimeout(resolve, 200); // Additional 200ms delay
        }, { timeout: 1000 });
      });
    } else {
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    // Load data silently in background (non-blocking)
    this.fetchData().then(() => {
      console.log('[REC MODAL] Preload completed:', {
        bestsellers: this.data.bestsellers?.length || 0,
        featured: this.data.featured?.length || 0,
        newarrivals: this.data.newarrivals?.length || 0
      });
    }).catch(error => {
      console.warn('[REC MODAL] Preload failed (will retry when modal opens):', error);
      // Preload failure doesn't affect functionality, will retry when modal opens
    });
  }

  /**
   * Check if a tab has products
   */
  hasTabProducts(tabId) {
    if (!this.data) return false;
    const products = this.data[tabId];
    return products && Array.isArray(products) && products.length > 0;
  }

  /**
   * Get available tabs (tabs that have products)
   */
  getAvailableTabs() {
    const allTabs = [
      { id: 'bestsellers', label: this.settings.messages.bestSellers },
      { id: 'featured', label: this.settings.messages.featured },
      { id: 'newarrivals', label: this.settings.messages.newArrivals }
    ];
    
    // Filter tabs that have products
    return allTabs.filter(tab => this.hasTabProducts(tab.id));
  }

  /**
   * Build HTML structure for cart notification injection only
   * No popup modal, only build body structure for injection
   */
  buildHTML() {
    // Get available tabs (only tabs with products)
    const availableTabs = this.getAvailableTabs();
    
    // If no tabs available, don't create body
    if (availableTabs.length === 0) {
      this.body = null;
      return;
    }

    // Body structure for cart notification injection
    const body = document.createElement('div');
    body.className = 'rec-body';
    this.body = body; // Save reference for cart notification injection

    // Sidebar
    const sidebar = document.createElement('div');
    sidebar.className = 'rec-sidebar';
    const nav = document.createElement('nav');
    nav.className = 'rec-nav';
    nav.setAttribute('role', 'tablist');
    
    // Only create tabs for available tabs
    availableTabs.forEach((tab, index) => {
      const tabBtn = document.createElement('button');
      tabBtn.type = 'button';
      tabBtn.className = `rec-nav-btn ${index === 0 ? 'is-active' : ''}`;
      tabBtn.setAttribute('role', 'tab');
      tabBtn.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      tabBtn.setAttribute('aria-controls', `rec-panel-${tab.id}`);
      tabBtn.setAttribute('data-tab', tab.id);
      tabBtn.textContent = tab.label;
      nav.appendChild(tabBtn);
    });

    sidebar.appendChild(nav);
    body.appendChild(sidebar);

    // Content area
    const content = document.createElement('div');
    content.className = 'rec-content';

    // Only create panels for available tabs
    availableTabs.forEach((tab, index) => {
      const panel = document.createElement('div');
      panel.id = `rec-panel-${tab.id}`;
      panel.className = `rec-panel ${index === 0 ? 'is-active' : ''}`;
      panel.setAttribute('role', 'tabpanel');
      if (index !== 0) {
        panel.hidden = true;
      }

      const grid = document.createElement('div');
      grid.className = 'rec-grid';
      grid.setAttribute('data-tab', tab.id);
      grid.innerHTML = `
        <div class="rec-loading">
          <div class="rec-spinner"></div>
          <span>${this.settings.messages.loading}</span>
        </div>
      `;

      panel.appendChild(grid);
      content.appendChild(panel);
    });

    body.appendChild(content);

    // Notification (for add to cart feedback)
    this.notification = document.createElement('div');
    this.notification.className = 'rec-notification';
    document.body.appendChild(this.notification);
  }

  /**
   * Attach event listeners (no popup, only for cart notification)
   */
  attachEvents() {
    // No popup events needed, events are attached in attachEventsToClonedContent
  }

  /**
   * Show modal (disabled - no popup, only cart notification injection)
   */
  async show() {
    // Popup disabled, only inject into cart notification
    return;
  }

  /**
   * Hide modal (disabled - no popup)
   */
  hide() {
    // Popup disabled
    return;
  }

  /**
   * Inject rec-body content into cart-notification-upsell
   * This method copies the recommendation products content to cart notification
   */
  async injectIntoCartNotification() {
    // Prevent duplicate injections
    if (this._injecting) {
      return;
    }
    
    // Skip injection if flag is set (e.g., from upsell button clicks)
    if (window.__showCartNotificationProduct) {
      return;
    }
    
    // Check if product has material metafield
    // If not, show cart-notification-product instead
    if (window.hasMaterialMetafield === false || (window.hasMaterialMetafield === undefined && !this.product)) {
      const cartNotificationProduct = document.getElementById('cart-notification-product');
      const cartNotificationUpsell = document.getElementById('cart-notification-upsell');
      if (cartNotificationProduct) {
        cartNotificationProduct.style.display = '';
      }
      if (cartNotificationUpsell) {
        cartNotificationUpsell.style.display = 'none';
      }
      return;
    }
    
    const cartNotificationUpsell = document.getElementById('cart-notification-upsell');
    if (!cartNotificationUpsell) {
      return;
    }
    
    // Check if already injected
    if (cartNotificationUpsell.querySelector('.rec-body')) {
      return;
    }
    
    this._injecting = true;

    try {
      // Ensure data is loaded
      if (!this.loaded && !this.loading) {
        await this.preload();
      } else if (this.loading) {
        // Wait for loading to complete
        await this.loadingPromise;
      }

      // Check if we have any products to show
      const hasProducts = this.data && (
        (this.data.bestsellers && this.data.bestsellers.length > 0) ||
        (this.data.featured && this.data.featured.length > 0) ||
        (this.data.newarrivals && this.data.newarrivals.length > 0)
      );

      if (!hasProducts) {
        // No products to show, show cart-notification-product instead
        const cartNotificationProduct = document.getElementById('cart-notification-product');
        if (cartNotificationProduct) {
          cartNotificationProduct.style.display = '';
        }
        cartNotificationUpsell.style.display = 'none';
        cartNotificationUpsell.classList.remove('has-content');
        return;
      }

    // Build the body structure if not already built
    if (!this.body) {
      // Get available tabs (only tabs with products)
      const availableTabs = this.getAvailableTabs();
      
      // If no tabs available, show cart-notification-product instead
      if (availableTabs.length === 0) {
        const cartNotificationProduct = document.getElementById('cart-notification-product');
        if (cartNotificationProduct) {
          cartNotificationProduct.style.display = '';
        }
        cartNotificationUpsell.style.display = 'none';
        cartNotificationUpsell.classList.remove('has-content');
        return;
      }
      
      // Create a temporary container to build the body
      const tempContainer = document.createElement('div');
      const body = document.createElement('div');
      body.className = 'rec-body';
      
      // Build sidebar
      const sidebar = document.createElement('div');
      sidebar.className = 'rec-sidebar';
      const nav = document.createElement('nav');
      nav.className = 'rec-nav';
      nav.setAttribute('role', 'tablist');
      
      // Only create tabs for available tabs
      availableTabs.forEach((tab, index) => {
        const tabBtn = document.createElement('button');
        tabBtn.type = 'button';
        tabBtn.className = `rec-nav-btn ${index === 0 ? 'is-active' : ''}`;
        tabBtn.setAttribute('role', 'tab');
        tabBtn.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        tabBtn.setAttribute('aria-controls', `rec-panel-${tab.id}`);
        tabBtn.setAttribute('data-tab', tab.id);
        tabBtn.textContent = tab.label;
        nav.appendChild(tabBtn);
      });

      sidebar.appendChild(nav);
      body.appendChild(sidebar);

      // Build content area
      const content = document.createElement('div');
      content.className = 'rec-content';

      // Only create panels for available tabs
      availableTabs.forEach((tab, index) => {
        const panel = document.createElement('div');
        panel.id = `rec-panel-${tab.id}`;
        panel.className = `rec-panel ${index === 0 ? 'is-active' : ''}`;
        panel.setAttribute('role', 'tabpanel');
        if (index !== 0) {
          panel.hidden = true;
        }

        const grid = document.createElement('div');
        grid.className = 'rec-grid';
        grid.setAttribute('data-tab', tab.id);
        grid.innerHTML = `
          <div class="rec-loading">
            <div class="rec-spinner"></div>
            <span>${this.settings.messages.loading}</span>
          </div>
        `;

        panel.appendChild(grid);
        content.appendChild(panel);
      });

      body.appendChild(content);
      this.body = body;
    }

    // Check if body has content before cloning
    if (!this.body || this.body.children.length === 0) {
      // Body is empty or null, show cart-notification-product instead
      const cartNotificationProduct = document.getElementById('cart-notification-product');
      if (cartNotificationProduct) {
        cartNotificationProduct.style.display = '';
      }
      cartNotificationUpsell.style.display = 'none';
      cartNotificationUpsell.classList.remove('has-content');
      return;
    }

    // Hide cart-notification-product first to prevent flash
    const cartNotificationProduct = document.getElementById('cart-notification-product');
    if (cartNotificationProduct) {
      cartNotificationProduct.style.display = 'none';
    }
    
    // Add injecting class to prevent :empty selector from triggering
    cartNotificationUpsell.classList.add('injecting');
    
    // Clone the rec-body content
    const bodyClone = this.body.cloneNode(true);
    
    // Clear existing content and append new content
    cartNotificationUpsell.innerHTML = '';
    cartNotificationUpsell.appendChild(bodyClone);
    
    // Remove injecting class and add has-content class after content is added
    cartNotificationUpsell.classList.remove('injecting');
    cartNotificationUpsell.classList.add('has-content');
    
    // Load initial tab content
    const activeTab = bodyClone.querySelector('.rec-nav-btn.is-active');
    if (activeTab) {
      const tabName = activeTab.dataset.tab;
      const grid = bodyClone.querySelector(`.rec-grid[data-tab="${tabName}"]`);
      if (grid) {
        await this.loadTabForContainer(tabName, grid);
      }
    }
    
    // Re-attach event listeners for the cloned elements
    this.attachEventsToClonedContent(cartNotificationUpsell);
    
    // Ensure cart-notification-product is hidden and upsell is visible
    if (cartNotificationProduct) {
      cartNotificationProduct.style.display = 'none';
    }
    cartNotificationUpsell.style.display = '';
    } finally {
      this._injecting = false;
    }
  }

  /**
   * Attach event listeners to cloned content in cart notification
   */
  attachEventsToClonedContent(container) {
    // Re-attach tab navigation events
    const navButtons = container.querySelectorAll('.rec-nav-btn');
    navButtons.forEach(btn => {
      // Remove any existing listeners first
      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);
      
      newBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const tabName = newBtn.dataset.tab;
        if (tabName) {
          this.switchTabInCartNotification(tabName, container);
        }
      });
    });

    // Re-attach add to cart buttons
    const addButtons = container.querySelectorAll('.rec-add-btn');
    addButtons.forEach(btn => {
      // Remove any existing listeners first
      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);
      
      newBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.addToCart(e, newBtn);
      });
    });
  }

  /**
   * Switch tab in cart notification
   */
  switchTabInCartNotification(tabName, container) {
    console.log('[REC MODAL] Switching tab in cart notification:', tabName);
    
    // Update active tab button
    const navButtons = container.querySelectorAll('.rec-nav-btn');
    navButtons.forEach(btn => {
      const isActive = btn.dataset.tab === tabName;
      if (isActive) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
      } else {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-selected', 'false');
      }
    });

    // Update active panel
    const panels = container.querySelectorAll('.rec-panel');
    panels.forEach(panel => {
      const isActive = panel.id === `rec-panel-${tabName}`;
      if (isActive) {
        panel.classList.add('is-active');
        panel.hidden = false;
      } else {
        panel.classList.remove('is-active');
        panel.hidden = true;
      }
    });

    // Load tab content if not already loaded
    const grid = container.querySelector(`.rec-grid[data-tab="${tabName}"]`);
    if (grid) {
      const hasContent = grid.querySelector('.rec-item') || grid.querySelector('.rec-empty');
      if (!hasContent) {
        this.loadTabForContainer(tabName, grid);
      }
    }
  }

  /**
   * Load tab content for cart notification container
   */
  async loadTabForContainer(tabName, grid) {
    if (this.cache[tabName]) {
      this.renderItems(grid, this.cache[tabName], tabName);
      return;
    }

    grid.innerHTML = `
      <div class="rec-loading">
        <div class="rec-spinner"></div>
        <span>${this.settings.messages.loading}</span>
      </div>
    `;

    try {
      if (!this.loaded) {
        await this.fetchData();
      }

      const products = this.data[tabName] || [];
      if (products.length > 0) {
        const limitedProducts = products.slice(0, 10);
        this.cache[tabName] = limitedProducts;
        this.renderItems(grid, limitedProducts, tabName);
      } else {
        grid.innerHTML = `
          <div class="rec-empty">
            ${this.settings.messages.noProducts}
          </div>
        `;
      }
    } catch (error) {
      console.error('[REC MODAL] Error loading products:', error);
      grid.innerHTML = `
        <div class="rec-empty">
          ${this.settings.messages.noProducts}
        </div>
      `;
    }
  }

  /**
   * Get cache key for current product
   */
  getCacheKey() {
    return `${this.cacheKey}_${this.product.handle}`;
  }

  /**
   * Get cached data
   */
  getCachedData() {
    try {
      const cacheKey = this.getCacheKey();
      const cached = sessionStorage.getItem(cacheKey);
      if (!cached) return null;

      const parsed = JSON.parse(cached);
      const now = Date.now();

      // Check if expired
      if (now - parsed.timestamp > this.cacheExpiry) {
        sessionStorage.removeItem(cacheKey);
        return null;
      }

      console.log('[REC MODAL] Using cached data for', this.product.handle);
      return parsed.data;
    } catch (error) {
      console.warn('[REC MODAL] Error reading cache:', error);
      return null;
    }
  }

  /**
   * Save data to cache
   */
  saveToCache(data) {
    try {
      const cacheKey = this.getCacheKey();
      const cacheData = {
        timestamp: Date.now(),
        data: data
      };
      sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
      console.log('[REC MODAL] Data cached for', this.product.handle);
    } catch (error) {
      console.warn('[REC MODAL] Error saving cache:', error);
      // sessionStorage might be full, ignore error
    }
  }

  /**
   * Fetch data from API
   */
  async fetchData() {
    if (this.loaded || !this.product) return;
    
    // Check cache first
    const cachedData = this.getCachedData();
    if (cachedData) {
      this.data = {
        bestsellers: (cachedData.bestsellers || []).map(p => this.normalizeProduct(p)),
        featured: (cachedData.featured || []).map(p => this.normalizeProduct(p)),
        newarrivals: (cachedData.newarrivals || []).map(p => this.normalizeProduct(p))
      };
      this.loaded = true;
      console.log('[REC MODAL] Loaded from cache:', {
        bestsellers: this.data.bestsellers?.length || 0,
        featured: this.data.featured?.length || 0,
        newarrivals: this.data.newarrivals?.length || 0
      });
      return;
    }
    
    // If currently loading, return existing Promise (prevent concurrent requests)
    if (this.loading && this.loadingPromise) {
      return this.loadingPromise;
    }

    // Start loading
    this.loading = true;
    this.loadingPromise = this._doFetchData();
    
    try {
      await this.loadingPromise;
      // Save to cache after successful load
      this.saveToCache({
        bestsellers: this.data.bestsellers,
        featured: this.data.featured,
        newarrivals: this.data.newarrivals
      });
    } finally {
      this.loading = false;
      this.loadingPromise = null;
    }
  }

  /**
   * Internal method to actually fetch data
   */
  async _doFetchData() {
    try {
      console.log('[REC MODAL] Loading data from API...');

      const endpoint = this.settings.apiEndpoint.split('?')[0];
      const url = `${endpoint}?productHandle=${encodeURIComponent(this.product.handle)}`;
      const response = await fetch(url, { method: 'GET' });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.tabs) {
        this.data = {
          bestsellers: (result.tabs.bestsellers || []).map(p => this.normalizeProduct(p)),
          featured: (result.tabs.featured || []).map(p => this.normalizeProduct(p)),
          newarrivals: (result.tabs.newarrivals || []).map(p => this.normalizeProduct(p))
        };

        this.loaded = true;
        console.log('[REC MODAL] API data loaded:', {
          bestsellers: this.data.bestsellers?.length || 0,
          featured: this.data.featured?.length || 0,
          newarrivals: this.data.newarrivals?.length || 0
        });
      } else {
        throw new Error('Invalid API response');
      }
    } catch (error) {
      console.error('[REC MODAL] Error loading data from API:', error);
      this.data = {
        bestsellers: [],
        featured: [],
        newarrivals: []
      };
      this.loaded = true;
      throw error; // Re-throw error so caller knows loading failed
    }
  }

  /**
   * Update tab visibility (deprecated - no popup, only used for cart notification)
   */
  updateTabVisibility() {
    // No longer needed for popup, tabs are handled in cart notification
  }

  /**
   * Switch tab (deprecated - no popup, only used for cart notification)
   */
  switchTab(tabName, loadIfNeeded = true) {
    // No longer needed for popup, tabs are handled in cart notification
  }

  /**
   * Load tab content (deprecated - no popup, only used for cart notification)
   */
  async loadTab(tabName) {
    // No longer needed for popup, tabs are handled in cart notification
  }

  /**
   * Normalize product data format
   */
  normalizeProduct(product) {
    return {
      id: product.id,
      handle: product.handle,
      title: product.title,
      url: product.url || `/products/${product.handle}`,
      image: product.image || '',
      avatar: product.avatar || null, // 添加 avatar 字段
      price: product.price || '',
      compareAtPrice: product.compareAtPrice || '',
      available: product.available !== false,
      createdAt: product.createdAt || new Date().getTime() / 1000,
      collectionId: product.collectionId || '',
      collectionHandle: product.collectionHandle || '',
      collectionTitle: product.collectionTitle || '',
      materials: product.materials || [],
      series: product.series || [],
      styles: product.styles || [],
      isExact: product.isExact || false,
      salesCount: product.salesCount || 0
    };
  }

  /**
   * Render product items
   */
  renderItems(container, products, tabName) {
    if (!products || products.length === 0) {
      container.innerHTML = `
        <div class="rec-empty">
          ${this.settings.messages.noProducts}
        </div>
      `;
      return;
    }

    const itemsHTML = products.map(product => this.renderItem(product)).join('');
    container.innerHTML = itemsHTML;

    // Bind add to cart events
    container.querySelectorAll('.rec-add-btn').forEach(btn => {
      btn.addEventListener('click', (e) => this.addToCart(e, btn));
    });
  }

  /**
   * Render single product item
   */
  renderItem(product) {
    const productUrl = product.url || `/products/${product.handle}`;
    // 优先使用 avatar metafield 的图片，如果没有则使用默认图片
    const imageUrl = product.avatar || product.image || 'https://via.placeholder.com/100';
    
    // 调试：输出图片 URL 来源
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1')) {
      console.log(`[DEBUG] Product ${product.handle}:`, {
        avatar: product.avatar,
        image: product.image,
        finalImageUrl: imageUrl,
        usingAvatar: !!product.avatar
      });
    }

    return `
      <div class="rec-item" data-handle="${product.handle}">
        <div class="rec-item-image">
          <img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" loading="lazy" style="width: 100px; height: 100px; object-fit: cover;">
        </div>
        <div class="rec-item-info">
          <a href="${productUrl}" class="rec-item-title" target="_blank">
            ${this.escapeHtml(product.title)}
          </a>
          <div class="rec-item-price">
            ${this.escapeHtml(product.price)}
          </div>
        </div>
        <div class="rec-item-actions">
          <button type="button" class="rec-add-btn" data-handle="${product.handle}" aria-label="Add to cart">
            <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
              <path d="M512 0C229.7 0 0 229.7 0 512s229.7 512 512 512 512-229.7 512-512S794.3 0 512 0z m0 979.5C254.2 979.5 44.5 769.8 44.5 512S254.2 44.5 512 44.5 979.5 254.2 979.5 512 769.8 979.5 512 979.5z"/>
              <path d="M779.2 489.7H534.3V244.9c0-12.3-10.1-22.3-22.3-22.3-12.3 0-22.3 10.1-22.3 22.3v244.8H244.9c-12.3 0-22.3 10.1-22.3 22.3 0 12.3 10.1 22.3 22.3 22.3h244.8v244.9c0 12.3 10.1 22.3 22.3 22.3 12.3 0 22.3-10.1 22.3-22.3V534.3h244.8c12.3 0 22.3-10.1 22.4-22.3 0-12.3-10.1-22.3-22.3-22.3z"/>
            </svg>
          </button>
        </div>
      </div>
    `;
  }

  /**
   * HTML escape
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Show notification
   */
  showNotification(message, type = 'info') {
    if (!this.notification) return;

    this.notification.classList.remove(
      'is-success',
      'is-error',
      'is-info',
      'is-warning'
    );

    if (type) {
      this.notification.classList.add(`is-${type}`);
    }

    this.notification.textContent = message;
    this.notification.classList.add('is-visible');

    setTimeout(() => {
      this.notification.classList.remove('is-visible');
    }, 2500);
  }

  /**
   * Add to cart handler
   */
  async addToCart(e, btn) {
    e.preventDefault();

    const handle = btn.dataset.handle;
    if (!handle || btn.disabled) return;

    if (this.addedItems.has(handle)) {
      this.showNotification(this.settings.messages.alreadyInCart, 'info');
      return;
    }

    console.log('[REC MODAL] Adding product to cart:', handle);

    btn.disabled = true;
    btn.classList.add('is-loading');

    try {
      // Fetch product data
      const productResponse = await fetch(`/products/${handle}.js`);
      const productData = await productResponse.json();

      if (!productData.variants || productData.variants.length === 0) {
        throw new Error('No variants found');
      }

      const variant = productData.variants.find(v => v.available) || productData.variants[0];

      // Add to cart request
      const formData = new FormData();
      formData.append('id', variant.id);
      formData.append('quantity', 1);

      // Try to use Shopify's fetchConfig, fallback to default
      let config = {};
      if (typeof fetchConfig === 'function') {
        config = fetchConfig('javascript');
        config.headers = config.headers || {};
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
      } else {
        config = {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        };
      }
      config.body = formData;

      const addUrl = this.settings.cartAddUrl;
      const response = await fetch(addUrl, config);
      const result = await response.json();

      if (result.status) {
        const errorMsg = result.description || result.message || '';
        if (errorMsg.toLowerCase().includes('already') || errorMsg.toLowerCase().includes('cart')) {
          this.showNotification(this.settings.messages.alreadyInCart, 'info');
          this.addedItems.add(handle);
          btn.classList.add('is-added');
        } else {
          this.showNotification(errorMsg || this.settings.messages.error, 'error');
        }
        btn.disabled = false;
        btn.classList.remove('is-loading');
        return;
      }

      console.log('[REC MODAL] Product added successfully');

      this.addedItems.add(handle);
      this.showNotification(this.settings.messages.addedToCart, 'success');

      btn.classList.remove('is-loading');
      btn.classList.add('is-success');
      btn.innerHTML = `
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      `;

      // Update cart count
      this.cartCount++;

      // Update page cart icon
      const cartBubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span, .cart-count, [data-cart-count]');
      if (cartBubble) {
        cartBubble.textContent = this.cartCount;
      }

      setTimeout(() => {
        btn.classList.add('is-added');
      }, 2000);

    } catch (error) {
      console.error('[REC MODAL] Error adding to cart:', error);
      this.showNotification(this.settings.messages.error, 'error');
      btn.disabled = false;
      btn.classList.remove('is-loading');
    }
  }
}

// Initialize function
function initRecommendationModal() {
  window.upsellPopup = new RecommendationModal(window.upsellPopupConfig);
  
  // Backward compatibility: expose open method
  if (window.upsellPopup) {
    window.upsellPopup.open = window.upsellPopup.show.bind(window.upsellPopup);
    window.upsellPopup.close = window.upsellPopup.hide.bind(window.upsellPopup);
    
    // Intercept cart-notification renderContents method
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification && cartNotification.renderContents) {
      const originalRenderContents = cartNotification.renderContents.bind(cartNotification);
      let renderContentsTimeout = null;
      cartNotification.renderContents = function(response) {
        const result = originalRenderContents(response);
        // Inject recommendation content after cart notification is rendered (with debounce)
        if (window.upsellPopup && window.upsellPopup.injectIntoCartNotification) {
          if (renderContentsTimeout) {
            clearTimeout(renderContentsTimeout);
          }
          renderContentsTimeout = setTimeout(() => {
            const cartNotificationUpsell = document.getElementById('cart-notification-upsell');
            if (cartNotificationUpsell && !cartNotificationUpsell.querySelector('.rec-body')) {
              window.upsellPopup.injectIntoCartNotification().catch(err => {
                console.error('[REC MODAL] Error injecting content:', err);
                // If injection fails, show cart-notification-product
                const cartNotificationProduct = document.getElementById('cart-notification-product');
                if (cartNotificationProduct) {
                  cartNotificationProduct.style.display = '';
                }
                if (cartNotificationUpsell) {
                  cartNotificationUpsell.style.display = 'none';
                }
              });
            }
          }, 500);
        } else {
          // If upsellPopup is not available, show cart-notification-product
          const cartNotificationProduct = document.getElementById('cart-notification-product');
          const cartNotificationUpsell = document.getElementById('cart-notification-upsell');
          if (cartNotificationProduct) {
            cartNotificationProduct.style.display = '';
          }
          if (cartNotificationUpsell) {
            cartNotificationUpsell.style.display = 'none';
            cartNotificationUpsell.classList.remove('has-content');
          }
        }
        return result;
      };
    }
    
    // Listen for cart add events
    setupCartListeners();
  }
}

// Setup cart event listeners
function setupCartListeners() {
  // Listen for cart-notification display and inject recommendation content
  const cartNotification = document.querySelector('cart-notification');
  if (cartNotification) {
    let injectTimeout = null;
    let lastInjected = 0;
    const INJECT_DEBOUNCE = 1000; // 1 second debounce
    
    // Also listen for the inner cart-notification div
    const innerCartNotification = document.getElementById('cart-notification');
    
    // Function to check and inject with debounce
    const checkAndInject = () => {
      const now = Date.now();
      if (now - lastInjected < INJECT_DEBOUNCE) {
        return; // Too soon, skip
      }
      
      const cartNotificationUpsell = document.getElementById('cart-notification-upsell');
      if (cartNotificationUpsell && cartNotificationUpsell.querySelector('.rec-body')) {
        return; // Already injected
      }
      
      const isVisible = cartNotification.style.display !== 'none' && 
                       window.getComputedStyle(cartNotification).display !== 'none';
      const innerVisible = innerCartNotification && 
                          innerCartNotification.style.display !== 'none' && 
                          window.getComputedStyle(innerCartNotification).display !== 'none';
      
      if ((isVisible || innerVisible) && window.upsellPopup && window.upsellPopup.injectIntoCartNotification) {
        // Clear existing timeout
        if (injectTimeout) {
          clearTimeout(injectTimeout);
        }
        
        // Debounce the injection
        injectTimeout = setTimeout(() => {
          lastInjected = Date.now();
          window.upsellPopup.injectIntoCartNotification().catch(err => {
            console.error('[REC MODAL] Error injecting content:', err);
          });
        }, 300);
      }
    };

    // Use MutationObserver to watch for cart-notification display changes
    const observer = new MutationObserver((mutations) => {
      // Only process if it's a style or class change on the main elements
      let shouldCheck = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'style' || mutation.attributeName === 'class') &&
            (mutation.target === cartNotification || mutation.target === innerCartNotification)) {
          shouldCheck = true;
        }
      });
      
      if (shouldCheck) {
        checkAndInject();
      }
    });

    observer.observe(cartNotification, {
      attributes: true,
      attributeFilter: ['style', 'class']
    });
    
    if (innerCartNotification) {
      observer.observe(innerCartNotification, {
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }

  }

    // Method 1: Listen for Shopify cart:updated event (only triggered after successful add)
    document.addEventListener('cart:updated', function(event) {
      console.log('[REC MODAL] Cart updated event detected');
      // Skip if flag is set (e.g., from upsell button clicks)
      if (window.__showCartNotificationProduct) {
        return;
      }
      // Inject into cart notification (only if not already injected)
      if (window.upsellPopup && window.upsellPopup.injectIntoCartNotification) {
        setTimeout(() => {
          const cartNotificationUpsell = document.getElementById('cart-notification-upsell');
          if (cartNotificationUpsell && !cartNotificationUpsell.querySelector('.rec-body')) {
            window.upsellPopup.injectIntoCartNotification().catch(err => {
              console.error('[REC MODAL] Error injecting content:', err);
              // If injection fails, show cart-notification-product
              const cartNotificationProduct = document.getElementById('cart-notification-product');
              if (cartNotificationProduct) {
                cartNotificationProduct.style.display = '';
              }
              if (cartNotificationUpsell) {
                cartNotificationUpsell.style.display = 'none';
              }
            });
          }
        }, 800);
      } else {
        // If upsellPopup is not available (no material metafield), show cart-notification-product
        const cartNotificationProduct = document.getElementById('cart-notification-product');
        const cartNotificationUpsell = document.getElementById('cart-notification-upsell');
        if (cartNotificationProduct) {
          cartNotificationProduct.style.display = '';
        }
        if (cartNotificationUpsell) {
          cartNotificationUpsell.style.display = 'none';
          cartNotificationUpsell.classList.remove('has-content');
        }
      }
    });

    // Method 2: Listen for cart:build event (Shopify theme, triggered after successful add)
    document.addEventListener('cart:build', function(event) {
      console.log('[REC MODAL] Cart build event detected');
      // Skip if flag is set (e.g., from upsell button clicks)
      if (window.__showCartNotificationProduct) {
        return;
      }
      // Inject into cart notification (only if not already injected)
      if (window.upsellPopup && window.upsellPopup.injectIntoCartNotification) {
        setTimeout(() => {
          const cartNotificationUpsell = document.getElementById('cart-notification-upsell');
          if (cartNotificationUpsell && !cartNotificationUpsell.querySelector('.rec-body')) {
            window.upsellPopup.injectIntoCartNotification().catch(err => {
              console.error('[REC MODAL] Error injecting content:', err);
              // If injection fails, show cart-notification-product
              const cartNotificationProduct = document.getElementById('cart-notification-product');
              if (cartNotificationProduct) {
                cartNotificationProduct.style.display = '';
              }
              if (cartNotificationUpsell) {
                cartNotificationUpsell.style.display = 'none';
              }
            });
          }
        }, 800);
      } else {
        // If upsellPopup is not available (no material metafield), show cart-notification-product
        const cartNotificationProduct = document.getElementById('cart-notification-product');
        const cartNotificationUpsell = document.getElementById('cart-notification-upsell');
        if (cartNotificationProduct) {
          cartNotificationProduct.style.display = '';
        }
        if (cartNotificationUpsell) {
          cartNotificationUpsell.style.display = 'none';
          cartNotificationUpsell.classList.remove('has-content');
        }
      }
    });

  // Method 3: Listen for form submissions to cart
  // Note: Don't prevent default submission, let form submit normally,
  // then rely on cart:updated event to show modal.
  // This ensures modal only shows after successful add.
  document.addEventListener('submit', function(event) {
    const form = event.target;
    if (!form) return;
    
    // Check if it's a cart add form
    const action = form.getAttribute('action') || '';
    const method = (form.getAttribute('method') || '').toLowerCase();
    
    if (action.includes('/cart/add') || 
        (action.includes('/cart') && method === 'post' && form.querySelector('[name="add"]'))) {
      
      console.log('[REC MODAL] Cart add form submitted, will wait for cart:updated event');
      // Don't prevent form submission, let it submit normally
      // cart:updated event will be triggered on success, handled by Method 1
    }
  }, true); // Use capture phase to catch early

  // Method 4: Listen for AJAX cart add (if using fetch) - only show after verifying success
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const url = args[0];
    if (typeof url === 'string' && url.includes('/cart/add')) {
      console.log('[REC MODAL] Cart add fetch detected, verifying success...');
      
      return originalFetch.apply(this, args).then(async response => {
        // Clone response to read it twice
        const clonedResponse = response.clone();
        
        // Check if response is successful
        if (response.ok) {
          try {
            const data = await clonedResponse.json();
            // Shopify returns {status: ...} for errors, !data.status means success
            if (!data.status) {
              console.log('[REC MODAL] Cart add successful via fetch');
              // Trigger cart update event
              document.dispatchEvent(new CustomEvent('cart:updated'));
              // Skip if flag is set (e.g., from upsell button clicks)
              if (window.__showCartNotificationProduct) {
                return response;
              }
              // Inject into cart notification (only if not already injected)
              if (window.upsellPopup && window.upsellPopup.injectIntoCartNotification) {
                setTimeout(() => {
                  const cartNotificationUpsell = document.getElementById('cart-notification-upsell');
                  if (cartNotificationUpsell && !cartNotificationUpsell.querySelector('.rec-body')) {
                    window.upsellPopup.injectIntoCartNotification().catch(err => {
                      console.error('[REC MODAL] Error injecting content:', err);
                      // If injection fails, show cart-notification-product
                      const cartNotificationProduct = document.getElementById('cart-notification-product');
                      if (cartNotificationProduct) {
                        cartNotificationProduct.style.display = '';
                      }
                      if (cartNotificationUpsell) {
                        cartNotificationUpsell.style.display = 'none';
                        cartNotificationUpsell.classList.remove('has-content');
                      }
                    });
                  }
                }, 800);
              } else {
                // If upsellPopup is not available (no material metafield), show cart-notification-product
                const cartNotificationProduct = document.getElementById('cart-notification-product');
                const cartNotificationUpsell = document.getElementById('cart-notification-upsell');
                if (cartNotificationProduct) {
                  cartNotificationProduct.style.display = '';
                }
                if (cartNotificationUpsell) {
                  cartNotificationUpsell.style.display = 'none';
                  cartNotificationUpsell.classList.remove('has-content');
                }
              }
            } else {
              console.warn('[REC MODAL] Cart add failed via fetch:', data);
              // Don't show modal on failure
            }
          } catch (error) {
            // If response is not JSON, might be HTML error page, don't show modal
            console.warn('[REC MODAL] Could not parse cart add response:', error);
          }
        } else {
          console.warn('[REC MODAL] Cart add HTTP error:', response.status);
          // Don't show modal on HTTP error
        }
        
        return response;
      });
    }
    return originalFetch.apply(this, args);
  };
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRecommendationModal);
} else {
  initRecommendationModal();
}

// Export for external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = RecommendationModal;
}
